import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { ApiService, ProductService, SortingService } from '@app-core/services';
import { filterProducts } from '@app-core/utils/filter-products';
import { UserResponse } from '@app-model/login';
import { GetProductsRequest } from '@app-model/request';
import { GetProductsResponse } from '@app-model/response';
import { SupportedLanguageCodes } from '@app-static-data/languages';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import * as ProductActions from './product.actions';
import * as fromRoot from '../../store/reducers';
import * as UserReducer from '../../store/user/user.reducer';
import * as CartActions from '../cart/cart.actions';

@Injectable()
export class ProductEffects {
  private actions$ = inject(Actions);
  private apiService = inject(ApiService);
  private store$ = inject<Store<fromRoot.State>>(Store);


  getProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.GET_PRODUCTS),
      withLatestFrom(this.store$.select((state) => state.userState)),
      mergeMap(([action, userState]: [ProductActions.GetProducts, UserReducer.State]) =>
        this.getProductsByClient(action.payload, userState.user).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            // sort plans alphanumerically
            response = SortingService.sortProductResponse(response);
            // adding surcharges if surcharge is selected
            response = ProductService.addSurchargesToProducts(action.payload, response);
            this.store$.dispatch(new CartActions.ResetCartAction());

            return new ProductActions.GetProductsSuccess(response, userState.markup);
          }),
          // If request fails, dispatch failed action
          catchError((error: HttpErrorResponse) =>
            of({ type: ProductActions.GET_PRODUCTS_FAILURE, payload: { error: error.statusText } })
          )
        )
      )
    )
  );

  filterGrid$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.FILTER_GRID),
      withLatestFrom(this.store$.select((state) => state.productState.response)),
      switchMap(([action, products]: [ProductActions.FilterGridAction, GetProductsResponse]) => {
        const allProducts = products.availableOptions.products.productItem;
        const grid = filterProducts(action.payload, allProducts);

        return of({ type: ProductActions.FILTER_GRID_SUCCESS, payload: { filters: action.payload, grid } });
      })
    )
  );

  filterBundle$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.FILTER_BUNDLE),
      withLatestFrom(this.store$.select((state) => state.productState.response)),
      switchMap(([action, products]: [ProductActions.FilterBundleAction, GetProductsResponse]) => {
        let allProducts = products.availableOptions.products.productItem;
        if (allProducts && allProducts.length) {
          allProducts = allProducts.sort((a, b) =>
            a.productDescription.enUsProductDescription > b.productDescription.enUsProductDescription ? 1 : -1
          );
        }
        const grid = filterProducts(action.payload, allProducts);

        return of({ type: ProductActions.FILTER_GRID_SUCCESS, payload: { filters: action.payload, grid } });
      })
    )
  );

  // Get Products based on client whether its CS 3.1 or Aggregator
  private getProductsByClient(request: GetProductsRequest, user: UserResponse): Observable<GetProductsResponse> {
    // add programId to request
    request.programId = user.programId;

    // call aggregator service if there are related dealers
    if (user.isRelatedDealer) {
      // set variable to request data for aggregator service
      const req = ProductService.aggregateProductRequestMapper(
        request,
        localStorage.getItem('languageCode') as SupportedLanguageCodes
      );

      return this.apiService.getProductsByUrl(user.aggregatorURL, req).pipe(
        switchMap((resp: GetProductsResponse) => {
          const response = ProductService.assignPropertiesToProducts(resp, user);

          return of(response);
        })
      );
    }

    // by default, call CS 3.1
    return this.apiService.getProducts(request);
  }
}
